// Frittstående C2-panel (poppet ut): innboks over stridsmeldinger + tråd-visning
// per melding (toveis dialog + status). Egen WS-feed (cookie-auth); meldinger og
// svar flyter via hubbens collab-fanout og flettes konfliktfritt. Klikk en
// melding → åpne case/tråd; «⌖» fokuserer i hovedkartet via SyncBus.
import {
  parseReports, mergeReport, reportSummary, STATUS_LABEL, STATUS_COLOR,
  type CombatReport, type ReportStatus,
} from '@skade/contracts';
import type { User } from '@skade/rbac';
import type { Feed } from '../data/feed.js';
import type { SyncBus } from '../sync.js';
import { ReportThreadView } from '../ui/report-thread.js';

export function mountC2Panel(root: HTMLElement, feed: Feed, sync: SyncBus, user: User): void {
  document.body.classList.add('panel-standalone');
  root.innerHTML = '';
  const wrap = document.createElement('div');
  wrap.className = 'standalone-panel';
  const head = document.createElement('div');
  head.className = 'standalone-head';
  head.append(txt('brand', 'SKADE'), txt('standalone-title', 'Stridsmeldinger'));
  const count = txt('roster-count', '0');
  head.append(count);
  const list = document.createElement('div');
  list.className = 'roster-list standalone-list';
  const threadHost = document.createElement('div');
  threadHost.className = 'thread-host embedded';
  wrap.append(head, list, threadHost);
  root.append(wrap);

  const reports = new Map<string, CombatReport>();
  let openId: string | null = null;
  let view: ReportThreadView | null = null;

  const broadcast = (r: CombatReport) => feed.send?.({ type: 'report', report: r });
  const upsert = (incoming: CombatReport, share: boolean): CombatReport => {
    const existing = reports.get(incoming.id);
    const merged = existing ? mergeReport(existing, incoming) : incoming;
    reports.set(incoming.id, merged);
    if (share) broadcast(merged);
    if (openId === merged.id) view?.update(merged);
    renderList();
    return merged;
  };
  const mutate = (id: string, fn: (r: CombatReport) => CombatReport) => {
    const cur = reports.get(id);
    if (cur) upsert({ ...fn(cur), updatedMs: Date.now() }, true);
  };

  function openThread(r: CombatReport) {
    openId = r.id;
    sync.post({ type: 'focus', lon: r.lon, lat: r.lat, label: r.type }); // fokusér i hovedkartet
    view = new ReportThreadView(reports.get(r.id) ?? r, {
      onStatus: (s: ReportStatus) => mutate(r.id, (rep) => ({ ...rep, status: s })),
      onComment: (text) => mutate(r.id, (rep) => ({
        ...rep,
        thread: [...rep.thread, { id: `c-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`, author: user.id, tsMs: Date.now(), text }],
      })),
      onGoto: () => { const cur = reports.get(r.id); if (cur) sync.post({ type: 'focus', lon: cur.lon, lat: cur.lat, label: cur.type }); },
      onClose: () => { openId = null; view = null; threadHost.classList.remove('open'); threadHost.innerHTML = ''; },
    });
    threadHost.innerHTML = '';
    threadHost.appendChild(view.el);
    threadHost.classList.add('open');
  }

  function renderList() {
    const arr = [...reports.values()].sort((a, b) => b.tsMs - a.tsMs);
    count.textContent = String(arr.length);
    list.innerHTML = '';
    if (!arr.length) { list.append(txt('roster-empty', 'Ingen meldinger ennå')); return; }
    for (const r of arr) {
      const row = document.createElement('button');
      row.className = 'roster-row';
      const dot = document.createElement('span'); dot.className = 'roster-dot';
      dot.style.background = STATUS_COLOR[r.status] ?? '#5b9bd5';
      const name = txt('roster-name', reportSummary(r));
      const kind = txt('roster-kind', `${r.type} · ${STATUS_LABEL[r.status]}`);
      row.append(dot, name, kind);
      row.onclick = () => openThread(r);
      list.append(row);
    }
  }

  feed.onCollab?.((m) => {
    if (m.kind !== 'report' || !m.report) return;
    for (const r of parseReports([m.report])) upsert(r, false);
  });
  feed.start();
  renderList();
}

function txt(cls: string, text: string): HTMLElement {
  const e = document.createElement('span');
  e.className = cls;
  e.textContent = text;
  return e;
}
